import { daysCounter } from '@oskecare/fe-utilities/scripts/helpers.js';
import BillingPeriod from './BillingPeriod.js';
import {
  ANNEX,
  BILLING_DOCUMENT_ATTACHMENT_STATUS_GENERATED,
  BILLING_DOCUMENT_ATTACHMENT_STATUS_GENERATING,
  BILLING_DOCUMENT_ATTACHMENT_STATUS_NOT_GENERATED,
  BILLING_DOCUMENTS_STATE_TO_STATUS,
  BILLING_DOCUMENT_TYPE_FE,
  BILLING_DOCUMENT_TYPE_FR,
  DELAY_FOR_HTML_PDF_GENERATION,
  DOCUMENT_TYPES_WITH_PENDING_STATE,
  BILLING_DOCUMENT_MIME_TYPE_HTML,
  BILLING_DOCUMENT_MIME_TYPE_PDF,
  INV_MAIN_CONS,
  ONLYCN_TYPE_ID,
  ONLYSN_TYPE_ID,
  PUBLIC_TYPE_ID,
  BILLING_DOCUMENT_STATUS_PAID,
  BILLING_DOCUMENT_STATUS_PARTIALLY_PAID,
  BILLING_DOCUMENT_STATUS_NOT_PAID,
} from '../constants.js';

/**
 * @property {string} id
 * @property {string} name
 * @property {string} type
 * @property {string} billNo
 * @property {Date} billDate
 * @property {BillingPeriod} period
 * @property {Date} dueDate
 * @property {number} amountDue
 * @property {number} amountRemaining
 * @property {{id: string, name: string, mimeType: string, url:string}[]} billDocument
 * @property {string} _state
 * @property {string} _invoiceBaseUrl
 */
export default class BillingDocument {
  /**
   *
   * @param {string} id
   * @param {string} category
   * @param {string} reference
   * @param {string} billNo
   * @param {string} billDate
   * @param {{startDateTime: string, endDateTime: string}} billingPeriod
   * @param {string} paymentDueDate
   * @param {{value: number, unit: string}} amountDue
   * @param {{value: number, unit: string}} remainingAmount
   * @param {string} state
   * @param {{id: string, name: string, mimeType: string, url:string}[]} billDocument
   * @param {string} invoiceBaseUrl
   */
  constructor({
    id,
    category,
    reference,
    billNo,
    billDate,
    billingPeriod,
    paymentDueDate,
    amountDue,
    remainingAmount,
    state,
    billDocument = [],
    invoiceBaseUrl,
  }) {
    this.id = id;
    this.name = reference;
    this.type = category;
    this.billNo = billNo;
    this.billDate = new Date(billDate);
    this.billingPeriod = billingPeriod;
    this.dueDate = new Date(paymentDueDate);
    this.amountDue = amountDue.value;
    this.amountRemaining = remainingAmount.value;
    this.billDocument = billDocument;
    this.state = state;
    this._invoiceBaseUrl = invoiceBaseUrl;
  }

  /**
   * @returns {BillingPeriod}
   */
  get period() {
    return new BillingPeriod(this.billingPeriod);
  }

  /**
   * @returns {number}
   */
  get paymentStatus() {
    return BILLING_DOCUMENTS_STATE_TO_STATUS[this.state];
  }

  /**
   * @returns {boolean}
   */
  get isPaid() {
    return this.paymentStatus === BILLING_DOCUMENT_STATUS_PAID;
  }

  /**
   * @returns {boolean}
   */
  get isPartiallyPaid() {
    return this.paymentStatus === BILLING_DOCUMENT_STATUS_PARTIALLY_PAID;
  }

  /**
   * @returns {boolean}
   */
  get isUnpaid() {
    return this.paymentStatus === BILLING_DOCUMENT_STATUS_NOT_PAID;
  }

  /**
   * Whether the document is either prepaid or postpaid invoice
   *
   * @returns {boolean}
   */
  get isBasicInvoice() {
    return [BILLING_DOCUMENT_TYPE_FE, BILLING_DOCUMENT_TYPE_FR].includes(this.type);
  }

  /**
   * @returns {?{id: string, name: string, mimeType: string, url:string}}
   */
  get _invoiceHtml() {
    return (
      this.billDocument.find(
        document =>
          document.mimeType === BILLING_DOCUMENT_MIME_TYPE_HTML &&
          document.id.includes(PUBLIC_TYPE_ID),
      ) ?? null
    );
  }

  /**
   * Note that PDF attachment is needed for HTML url construction and therefore
   * documents without PDF attachment can't have HTML attachment
   *
   * @return {string}
   */
  get invoiceHtmlUrl() {
    if (!this._invoiceHtml || this._invoiceHtml.url.includes(ANNEX) || !this._invoicePdf) {
      return '';
    }
    return `${this._invoiceBaseUrl}/${this._invoicePdf.id}/${this._invoicePdf.name}/html`;
  }

  /**
   * @returns {?{id: string, name: string, mimeType: string, url:string}}
   */
  get _invoicePdf() {
    if (this.isBasicInvoice) {
      return (
        this.billDocument.find(
          document =>
            document.mimeType === BILLING_DOCUMENT_MIME_TYPE_PDF &&
            (document.id.includes(ONLYSN_TYPE_ID) || document.id.includes(ONLYCN_TYPE_ID)),
        ) ?? null
      );
    }
    return (
      this.billDocument.find(document => document.mimeType === BILLING_DOCUMENT_MIME_TYPE_PDF) ??
      null
    );
  }

  /**
   * @return {string}
   */
  get invoicePdfUrl() {
    if (!this._invoicePdf || this._invoicePdf.url.includes(ANNEX)) {
      return '';
    }
    return `${this._invoiceBaseUrl}/${this._invoicePdf.id}/${this._invoicePdf.name}/pdf`;
  }

  /**
   * @return {(0|1|-1)}
   */
  get invoicePdfStatus() {
    if (this.invoicePdfUrl) {
      return BILLING_DOCUMENT_ATTACHMENT_STATUS_GENERATED;
    }

    if (
      DOCUMENT_TYPES_WITH_PENDING_STATE.includes(this.type) &&
      daysCounter(this.billDate) < DELAY_FOR_HTML_PDF_GENERATION &&
      this.billDocument.every(document => !document.id.includes(INV_MAIN_CONS))
    ) {
      return BILLING_DOCUMENT_ATTACHMENT_STATUS_GENERATING;
    }

    return BILLING_DOCUMENT_ATTACHMENT_STATUS_NOT_GENERATED;
  }

  /**
   * @return {(0|1|-1)}
   */
  get invoiceHtmlStatus() {
    if (this.invoiceHtmlUrl) {
      return BILLING_DOCUMENT_ATTACHMENT_STATUS_GENERATED;
    }

    if (
      DOCUMENT_TYPES_WITH_PENDING_STATE.includes(this.type) &&
      daysCounter(this.billDate) < DELAY_FOR_HTML_PDF_GENERATION &&
      this.billDocument.every(document => !document.id.includes(INV_MAIN_CONS))
    ) {
      return BILLING_DOCUMENT_ATTACHMENT_STATUS_GENERATING;
    }

    return BILLING_DOCUMENT_ATTACHMENT_STATUS_NOT_GENERATED;
  }
}
